import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, Plus, Minus, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { fetchProducts, saveCart, getCart } from '../services/api';
import { getSessionId } from '../utils/session';
import ChatAssistant from '../components/ChatAssistant';
import axios from 'axios';
import './Home.css';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Home = () => {
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckoutChat, setShowCheckoutChat] = useState(false);
  const [autoOpenChatTimer, setAutoOpenChatTimer] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerDataComplete, setCustomerDataComplete] = useState(false);
  const sessionId = getSessionId();

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await fetchProducts();
        setProducts(data);
      } catch (_) { /* ignore */ }
      setLoading(false);
    };
    loadProducts();
    setCustomerDataComplete(false);
  }, []);

  useEffect(() => {
    if (showCart && !customerDataComplete && cart.length > 0 && !showCheckoutChat) {
      const timer = setTimeout(() => {
        setShowCheckoutChat(true);
      }, 2000);
      setAutoOpenChatTimer(timer);
      return () => clearTimeout(timer);
    }
  }, [showCart, customerDataComplete, cart.length, showCheckoutChat]);

  useEffect(() => {
    const loadCart = async () => {
      try {
        const cartData = await getCart(sessionId);
        if (cartData && cartData.items) {
          setCart(cartData.items);
        }
      } catch (_) { /* ignore */ }
    };
    loadCart();
  }, [sessionId]);

  useEffect(() => {
    if (cart.length > 0) {
      saveCart(sessionId, cart).catch(() => {});
    }
  }, [cart, sessionId]);

  const addToCart = (product, selectedSize = null) => {
    const cartItem = {
      id: selectedSize ? `${product.id}-${selectedSize.size}` : product.id,
      name: product.name,
      price: selectedSize ? selectedSize.price : product.price,
      size: selectedSize?.size,
      quantity: 1,
      image: product.image
    };

    const existingItem = cart.find(item => item.id === cartItem.id);

    if (existingItem) {
      setCart(cart.map(item =>
        item.id === cartItem.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, cartItem]);
    }
  };

  const removeFromCart = (itemId) => {
    const item = cart.find(i => i.id === itemId);
    if (item && item.quantity > 1) {
      setCart(cart.map(i =>
        i.id === itemId
          ? { ...i, quantity: i.quantity - 1 }
          : i
      ));
    } else {
      setCart(cart.filter(i => i.id !== itemId));
    }
  };

  const getTotalPrice = useCallback(() => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2);
  }, [cart]);

  const checkCustomerData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/customer-data/${sessionId}/missing`);
      setCustomerDataComplete(response.data.isComplete);
    } catch (_) { /* ignore */ }
  }, [sessionId]);

  useEffect(() => {
    if (cart.length > 0) {
      checkCustomerData();
      const interval = setInterval(checkCustomerData, 5000);
      return () => clearInterval(interval);
    }
  }, [cart.length, checkCustomerData, showCart]);

  const sendToWhatsApp = async () => {
    if (cart.length === 0) return;

    try {
      const response = await axios.get(`${API_URL}/customer-data/${sessionId}/missing`);

      if (!response.data.isComplete) {
        alert('Antes de enviar o pedido, por favor preencha seus dados com o Chef Empada!');
        return;
      }

      const customerData = response.data.data;
      let message = customerData._whatsapp_message;

      if (!message) {
        message = '*Pedido - Empadão da Ilha*\n\n';
        message += '*DADOS DO CLIENTE:*\n';
        message += `Nome: ${customerData.name}\n`;
        message += `Telefone: ${customerData.phone}\n`;
        message += `Endereço: ${customerData.address}\n`;
        message += `Bairro: ${customerData.neighborhood}\n`;
        if (customerData.cep) message += `CEP: ${customerData.cep}\n`;
        if (customerData.complement) message += `Complemento: ${customerData.complement}\n`;
        message += `\n*Pagamento:* ${customerData.paymentMethod}\n`;
        message += `*Entrega:* ${customerData.deliveryType}\n`;
        message += `*Data/Horário:* ${customerData.deliveryDateTime}\n`;
        if (customerData.observations) {
          message += `\n*Observações:* ${customerData.observations}\n`;
        }
        message += '\n*PEDIDO:*\n';
        cart.forEach(item => {
          message += `- ${item.quantity}x ${item.name}`;
          if (item.size) message += ` (${item.size})`;
          message += ` - R$ ${(item.price * item.quantity).toFixed(2)}\n`;
        });
        message += `\n*Total: R$ ${getTotalPrice()}*\n\nAguardo confirmação!`;
      }

      try {
        const orderItems = cart.map(item => ({
          productId: item.id,
          productName: item.name,
          size: item.size || null,
          quantity: item.quantity,
          unitPrice: item.price,
          totalPrice: item.price * item.quantity
        }));

        await axios.post(`${API_URL}/orders`, {
          sessionId,
          customerPhone: customerData.phone,
          customerName: customerData.name,
          customerEmail: customerData.email || null,
          deliveryAddress: customerData.address,
          deliveryNeighborhood: customerData.neighborhood,
          deliveryCep: customerData.cep || '',
          items: orderItems,
          totalAmount: parseFloat(getTotalPrice()),
          paymentMethod: customerData.paymentMethod,
          deliveryType: customerData.deliveryType,
          deliveryDateTime: customerData.deliveryDateTime,
          notes: customerData.observations || null
        });
      } catch (_) { /* order save failed, continue to WhatsApp */ }

      const whatsappPhone = process.env.REACT_APP_WHATSAPP_PHONE || '5548996929082';
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${whatsappPhone}&text=${encodeURIComponent(message)}`;

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.location.href = whatsappUrl;
      } else {
        window.open(whatsappUrl, '_blank');
      }
    } catch (_) {
      alert('Erro ao processar pedido. Tente novamente!');
    }
  };

  const categorizeProducts = () => {
    const categories = {};
    products.forEach(product => {
      if (!categories[product.category]) {
        categories[product.category] = [];
      }
      categories[product.category].push(product);
    });
    return categories;
  };

  const categorizedProducts = categorizeProducts();

  const allCategories = [
    { title: 'Empadões', items: categorizedProducts['Empadões'] || [] },
    { title: 'Empadas Salgadas', items: categorizedProducts['Empadas Salgadas'] || [], info: 'A partir de R$ 9,90' },
    { title: 'Empadas Doces', items: categorizedProducts['Empadas Doces'] || [], info: 'A partir de R$ 9,90' },
    { title: 'Quiches', items: categorizedProducts['Quiches'] || [] },
    { title: 'Tortas Frias', items: categorizedProducts['Tortas Frias'] || [] },
    { title: 'Sobremesas na Travessa', items: categorizedProducts['Sobremesas na Travessa'] || [] }
  ];

  if (loading) {
    return (
      <div className="home-container">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <p style={{ fontSize: '1.5rem', color: 'var(--color-brown)' }}>Carregando cardápio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section-new">
        <div className="hero-content-new">
          <div className="logo-container">
            <img
              src="https://customer-assets.emergentagent.com/job_smart-cart-23/artifacts/nmv0fccn_Logotipo%20Nome%20Monograma%20Minimalista%20Preto%20e%20Branco%2818%29.png"
              alt="Empadão da Ilha Logo"
              className="main-logo"
            />
          </div>
          <p className="brand-subtitle">Sabor artesanal que conquista Florianópolis</p>

          <div className="categories-grid">
            <div className="category-card" onClick={() => document.getElementById('empadas-section')?.scrollIntoView({ behavior: 'smooth' })}>
              <div className="category-icon">
                <img src="https://customer-assets.emergentagent.com/job_ilha-menu-festivo/artifacts/nsk8r2ah_WhatsApp%20Image%202025-10-28%20at%2014.58.42%281%29.jpeg" alt="Empadas" className="category-image" />
              </div>
              <h3>Empadas</h3>
            </div>
            <div className="category-card" onClick={() => document.getElementById('empadoes-section')?.scrollIntoView({ behavior: 'smooth' })}>
              <div className="category-icon">
                <img src="https://customer-assets.emergentagent.com/job_smart-cart-23/artifacts/fhk5i71g_WhatsApp%20Image%202025-10-01%20at%2019.19.51%284%29.jpeg" alt="Empadões" className="category-image" />
              </div>
              <h3>Empadões</h3>
            </div>
            <div className="category-card" onClick={() => document.getElementById('quiches-section')?.scrollIntoView({ behavior: 'smooth' })}>
              <div className="category-icon">
                <img src="https://customer-assets.emergentagent.com/job_ilha-menu-festivo/artifacts/7yo2gpp3_WhatsApp%20Image%202025-11-06%20at%2009.27.56%281%29.jpeg" alt="Quiches" className="category-image" />
              </div>
              <h3>Quiches</h3>
            </div>
            <div className="category-card" onClick={() => document.getElementById('sobremesas-section')?.scrollIntoView({ behavior: 'smooth' })}>
              <div className="category-icon">
                <img src="https://customer-assets.emergentagent.com/job_ilha-menu-festivo/artifacts/ca9dsov0_WhatsApp%20Image%202025-11-12%20at%2009.26.08%282%29.jpeg" alt="Sobremesas" className="category-image" />
              </div>
              <h3>Sobremesas</h3>
            </div>
            <div className="category-card" onClick={() => document.getElementById('empadoes-section')?.scrollIntoView({ behavior: 'smooth' })}>
              <div className="category-icon">
                <img src="https://customer-assets.emergentagent.com/job_smart-cart-23/artifacts/fhk5i71g_WhatsApp%20Image%202025-10-01%20at%2019.19.51%284%29.jpeg" alt="Congelados" className="category-image" />
              </div>
              <h3>Congelados</h3>
            </div>
          </div>

          <Button
            className="cta-button-new"
            onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="view-menu-btn"
          >
            Ver Cardápio Completo
          </Button>
        </div>
      </section>

      {/* Banner Seja Revendedor */}
      <section className="reseller-banner">
        <div className="reseller-content">
          <h2>Seja Nosso Revendedor</h2>
          <p>Tenha uma renda extra revendendo nossos produtos artesanais</p>
          <Button
            className="whatsapp-contact-button"
            onClick={() => window.open('https://api.whatsapp.com/send?phone=5548996929082&text=Olá! Tenho interesse em ser revendedor.', '_blank')}
            data-testid="reseller-whatsapp-btn"
          >
            Fale Conosco pelo WhatsApp
          </Button>
        </div>
      </section>

      {/* Seção Sobre */}
      <section className="about-section">
        <div className="about-content">
          <h2 className="about-title">Empadões e Empadas Artesanais em Florianópolis</h2>
          <div className="about-text">
            <p>
              No Empadão da Ilha, fazemos empadas e empadões artesanais com massa leve,
              recheios generosos e aquele sabor caseiro que conquista na primeira mordida.
              São receitas próprias, ingredientes selecionados e muito capricho em cada detalhe.
            </p>
            <p>
              Temos diversos sabores, incluindo opções especiais e sem trigo, além de tamanhos
              variados e versões congeladas, perfeitas para quem busca praticidade sem abrir
              mão da qualidade.
            </p>
            <p className="about-highlight">
              Se você procura empadas e empadões em Florianópolis com sabor de verdade e
              atendimento próximo, o Empadão da Ilha é a escolha certa.<br />
              Peça agora e experimente.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="products-section" data-testid="products-section">
        {allCategories.map(({ title, items, info }) => {
          const sectionIdMap = {
            'Empadões': 'empadoes-section',
            'Empadas Salgadas': 'empadas-section',
            'Empadas Doces': 'empadas-section',
            'Quiches': 'quiches-section',
            'Tortas Frias': 'tortas-section',
            'Sobremesas na Travessa': 'sobremesas-section',
          };
          const sectionId = sectionIdMap[title] || '';

          return (
            <div key={title} id={sectionId} className="category-section">
              <h2 className="category-title">
                {title}
                {info && <span className="category-info">{info}</span>}
              </h2>
              <div className="products-grid">
                {items.map(product => (
                  <Card key={product.id} className="product-card" data-testid={`product-card-${product.id}`}>
                    {product.badge && (
                      <div className="product-badge">{product.badge}</div>
                    )}
                    <div className="product-image-container">
                      <img src={product.image} alt={product.name} className="product-image" />
                    </div>
                    <CardContent className="product-content">
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-description">{product.description}</p>

                      {product.sizes ? (
                        <div className="product-sizes">
                          {product.sizes.map((sizeOption) => (
                            <div key={`${product.id}-${sizeOption.size}`} className="size-option">
                              <div className="size-info">
                                <span className="size-label">{sizeOption.size}</span>
                                <span className="size-price">R$ {sizeOption.price.toFixed(2)}</span>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => addToCart(product, sizeOption)}
                                className="add-button"
                                data-testid={`add-${product.id}-${sizeOption.size}`}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="product-price-container">
                          <span className="product-price">R$ {product.price.toFixed(2)}</span>
                          <Button
                            onClick={() => addToCart(product)}
                            className="add-button"
                            data-testid={`add-${product.id}`}
                          >
                            Adicionar <Plus className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      )}

                      {product.deliveryDates && (
                        <div className="delivery-info">
                          <small>Entregas: {product.deliveryDates.join(' ou ')}</small>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {/* Floating Cart Button */}
      <button
        className="floating-cart-button"
        onClick={() => setShowCart(!showCart)}
        data-testid="floating-cart-btn"
      >
        <ShoppingCart className="w-6 h-6" />
        {cart.length > 0 && (
          <Badge className="cart-badge" data-testid="cart-count">{cart.reduce((sum, item) => sum + item.quantity, 0)}</Badge>
        )}
      </button>

      {/* Cart Drawer */}
      {showCart && (
        <div className="cart-overlay" onClick={() => setShowCart(false)}>
          <div className="cart-drawer" onClick={(e) => e.stopPropagation()} data-testid="cart-drawer">
            <div className="cart-header">
              <h2>{showCheckoutChat ? 'Finalizando Pedido' : 'Seu Pedido'}</h2>
              <button onClick={() => {
                setShowCart(false);
                setShowCheckoutChat(false);
                if (autoOpenChatTimer) clearTimeout(autoOpenChatTimer);
              }} className="close-button" data-testid="close-cart-btn">✕</button>
            </div>

            {showCheckoutChat && (
              <div className="checkout-chat-container">
                <ChatAssistant
                  cart={cart}
                  embedded={true}
                  onDataComplete={() => {
                    setCustomerDataComplete(true);
                  }}
                  onSendToWhatsApp={sendToWhatsApp}
                />
                <button
                  className="back-to-cart-button"
                  onClick={() => setShowCheckoutChat(false)}
                  data-testid="back-to-cart-btn"
                >
                  Voltar pro Carrinho
                </button>
              </div>
            )}

            <div className={`cart-items ${showCheckoutChat ? 'with-chat' : ''}`}>
              {cart.length === 0 ? (
                <p className="empty-cart" data-testid="empty-cart">Seu carrinho está vazio</p>
              ) : (
                cart.map(item => (
                  <div key={item.id} className="cart-item" data-testid={`cart-item-${item.id}`}>
                    <img src={item.image} alt={item.name} className="cart-item-image" />
                    <div className="cart-item-info">
                      <h4>{item.name}</h4>
                      {item.size && <p className="cart-item-size">{item.size}</p>}
                      <p className="cart-item-price">R$ {item.price.toFixed(2)}</p>
                    </div>
                    <div className="cart-item-controls">
                      <button onClick={() => removeFromCart(item.id)} className="quantity-button" data-testid={`remove-${item.id}`}>
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button onClick={() => addToCart({ id: item.id, name: item.name, price: item.price, image: item.image }, item.size ? { size: item.size, price: item.price } : null)} className="quantity-button" data-testid={`add-more-${item.id}`}>
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && !showCheckoutChat && (
              <div className="cart-footer" data-testid="cart-footer">
                <div className="cart-total">
                  <span>Total</span>
                  <span className="total-price" data-testid="cart-total-price">R$ {getTotalPrice()}</span>
                </div>
                <Button
                  className="whatsapp-button always-enabled"
                  onClick={() => {
                    if (customerDataComplete) {
                      sendToWhatsApp();
                    } else {
                      setShowCheckoutChat(true);
                    }
                  }}
                  data-testid="send-whatsapp-btn"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Enviar Pedido pelo WhatsApp
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat Assistant */}
      <ChatAssistant cart={cart} />

      {/* Footer */}
      <footer className="footer">
        <p className="footer-text">Tradição, sabor e carinho direto da Ilha para sua mesa.</p>
        <p className="footer-brand">Empadão da Ilha - Florianópolis, SC</p>
        <div className="footer-contact">
          <a href={`https://api.whatsapp.com/send?phone=${process.env.REACT_APP_WHATSAPP_PHONE || '5548996929082'}`} target="_blank" rel="noopener noreferrer">
            (48) 99692-9082
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Home;
